import TextOptions from "%/dynamic-component-editor/bundles/text-options";
import { suppressesNavigation } from "%/dynamic-component-editor/helpers/builder-navigation-guard";
import { hasUnfilledPlaceholder } from "%/dynamic-component-editor/standard-elements/click-actions/resolve-context-href";
import { ContextHrefMixin } from "%/dynamic-component-editor/standard-elements/click-actions/context-href-mixin";
import { mapActions, mapState, mapWritableState } from "pinia";
import useDynamicComponentEditorStore from "%/dynamic-component-editor/stores/dynamic-component-editor";
import TextOption from "%/dynamic-component-editor/options/text-option";
import TextWrap from "%/dynamic-component-editor/options/text-wrap";
import HrefOption from "%/dynamic-component-editor/options/href-option";
import TextAlign from "%/dynamic-component-editor/options/text-align";
import Ellipsis from "%/dynamic-component-editor/options/ellipsis";
import TextDecoration from "%/dynamic-component-editor/options/text-decoration";
import Padding from "%/dynamic-component-editor/options/padding";
import Margins from "%/dynamic-component-editor/options/margins";
import BaseOption from "%/dynamic-component-editor/classes/base-option";
import DynamicElement from "%/dynamic-component-editor/classes/dynamic-element";
import FontFamily from "%/dynamic-component-editor/options/font-family";
import FontSize from "%/dynamic-component-editor/options/font-size";
import FontColor from "%/dynamic-component-editor/options/font-color";
import normalizeFontSizeStyleValue from "%/dynamic-component-editor/helpers/font-size-style-value";
import FontWeight from "%/dynamic-component-editor/options/font-weight";
import FontCase from "%/dynamic-component-editor/options/font-case";
import SpacingOptions from "%/dynamic-component-editor/bundles/spacing-options";
import FontStyle from "%/dynamic-component-editor/options/font-style";
import LineHeightMultiplier from "%/dynamic-component-editor/options/line-height-multiplier";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import BasicTextOptions from "%/dynamic-component-editor/bundles/basic-text-options";
import BrandedTextStyleOptions from "%/dynamic-component-editor/bundles/branded-text-style-options";
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import BackgroundColor from "%/dynamic-component-editor/options/background-color";
import BackgroundOptions from "%/dynamic-component-editor/bundles/background-options";
import Label from "%/dynamic-component-editor/options/label";
import EffectsOptions from "%/dynamic-component-editor/bundles/effects-options";
import BoxShadow from "%/dynamic-component-editor/options/box-shadow";
import BorderSides from "%/dynamic-component-editor/options/border-sides";
import MaxWidth from "%/dynamic-component-editor/options/max-width";
import MinWidth from "%/dynamic-component-editor/options/min-width";
import ClickAction from "%/dynamic-component-editor/options/click-action";
import LinkType from "%/dynamic-component-editor/options/link-type";
import { resolveContextualPageValue } from "%/dynamic-components/render-engine/options/click-actions/contextual-page-value";
import { isBuilderQuiet, pageLinkValueFor } from "%/dynamic-component-editor/standard-elements/click-actions/resolve-page-target";
import DynamicComponentError from "%/dynamic-component-editor/errors/dynamic-component-error";
import TextShadow from "%/dynamic-component-editor/options/text-shadow";
import TextOverflow from "%/dynamic-component-editor/options/text-overflow";
import MaxVerticalLines from "%/dynamic-component-editor/options/max-vertical-lines";
import { useCollaboratorStore } from "~/stores/collaborator-store";
import LayoutHeight from "%/dynamic-component-editor/options/layout-height";
import { GestureActionsMixin } from "%/dynamic-component-editor/standard-elements/click-actions/gesture-actions-mixin";

const CLICK_BUFFER = 1000;
// Debounce the canvas inline-edit option write so the heavy compile/persist
// runs after the typing burst, keeping keystrokes instant.
const TEXT_WRITE_DEBOUNCE_MS = 300;

// Compiled "no border" shape iText's border-css helper derefs;
// mirrors the layout mixin's NO_BORDER_SIDES.
const NO_BORDER_SIDE = Object.freeze({ width: "0", color: "transparent", style: "solid" });
const NO_TEXT_BORDER_SIDES = Object.freeze({
  top: NO_BORDER_SIDE,
  right: NO_BORDER_SIDE,
  bottom: NO_BORDER_SIDE,
  left: NO_BORDER_SIDE,
});

export const TextElementMixin = {
  mixins: [ContextHrefMixin, GestureActionsMixin],
  inject: {
    // Same gate as the button mixin: provided by DCBE so a text element
    // rendered inside a templated component (via DCPE with edit-mode=false)
    // still treats clicks as "select / drag" on the builder canvas rather
    // than firing the configured click action. Absent outside the
    // builder, where the runtime click action is the intended behaviour.
    inBuilderEditScope: {
      from: "inBuilderEditScope",
      default: false,
    },
  },
  elementPanelSettings: {
    displayIcon: "text",
    elementType: "element",
    elementGroup: "general",
  },
  optionBundles: [
    new AppearanceOptions({}, {
      "border-radius": false,
    }),
    BrandedTextStyleOptions,
    BasicTextOptions,
    TextOptions,
    EffectsOptions,
    new SpacingOptions({
      "default.padding": {
        "top": null,
        "left": null,
        "bottom": null,
        "right": null,
      },
    }),
    new BackgroundOptions({}, {
      "background-image": true,
      "background-gradient": true,
    }),
  ],
  options: [
    ClickAction,
  ],
  data() {
    return {
      initialValue: "",
      TextOption,
      TextWrap,
      TextAlign,
      Ellipsis,
      FontCase,
      HrefOption,
      lastClicked: 0,
      editable: false,
      relatedElement: null,
      debounce: null,
      textWriteTimer: null,
      showLinkModal: false,
      linkData: {},
      originalLinkData: {},
    };
  },
  computed: {
    ...mapWritableState(useDynamicComponentEditorStore, [
      "dynamicComponentName",
      "isPage",
    ]),
    ...mapState(useCollaboratorStore, [
      "canEditPublication",
    ]),
    fontColor() {
      return this.getOptionValue(FontColor);
    },
    fontSizeNumberValue() {
      return parseInt(this.getOptionValue(FontSize).replace("px", ""));
    },
    myWidth() {
      if (!this.getOptionValue(TextOverflow) && !this.getOptionValue(TextWrap)) {
        if (typeof this.getOptionValue(LayoutWidth) !== "number") {
          return "fill";
        }
      }
      return this.getOptionValue(LayoutWidth);
    },
    myHeight() {
      return this.getOptionValue(LayoutHeight);
    },
    fullTextOptionsAttributes() {
      return {
        width: this.myWidth,
        height: this.myHeight,
        maxWith: this.getOptionValue(MaxWidth),
        minWith: this.getOptionValue(MinWidth),
        ...this.textOnlyAttributes,
        textStyleOverrides: this.textStyleOverrides,
      };
    },
    textOnlyAttributes() {
      return {
        overflow: this.getOptionValue(TextOverflow),
        maxVerticalLines: this.getOptionValue(MaxVerticalLines),
        wrap: this.getOptionValue(TextWrap),
        textAlign: this.getOptionValue(TextAlign),
        textCase: this.getOptionValue(FontCase),
        lineHeightMultiplier: this.getOptionValue(LineHeightMultiplier),
        textStyleOverrides: this.textOnlyStyleOverrides,
      };
    },
    textOnlyStyleOverrides() {
      // Mirror text-without-branding-element-mixin's `|| "standard"` fallback.
      // When the resolver chain can't reach a leaf (e.g. preview render in a
      // session where the branding chain walk legitimately misses for an
      // option, or a paste from a publication using a deprecated theme key
      // that doesn't exist in the destination's theme), `getOptionValue`
      // may return undefined. iText's styleOverrides validator
      // (VCL `assertStyleValid`) rejects undefined with `"StyleValue is not
      // valid for the provided type"`, so swap in the theme's `"standard"`
      // alias as the safety net. Branded text edits always store a real
      // value, so the fallback only kicks in when the data is genuinely
      // missing.
      return {
        standardFontFamily: this.getOptionValue(FontFamily) || "standard",
        standardFontSize: normalizeFontSizeStyleValue(this.getOptionValue(FontSize), { element: "TextElement", option: "font-size" }),
        standardFontColor: this.getOptionValue(FontColor) || "standard",
        standardFontWeight: this.getOptionValue(FontWeight) || "standard",
        standardHoverFontFamily: this.getOptionValue(FontFamily, BaseOption.$states.HOVER) || "standard",
        standardHoverFontSize: normalizeFontSizeStyleValue(this.getOptionValue(FontSize, BaseOption.$states.HOVER), { element: "TextElement", option: "hover.font-size" }),
        standardHoverFontColor: this.getOptionValue(FontColor, BaseOption.$states.HOVER) || "standard",
        standardHoverFontWeight: this.getOptionValue(FontWeight, BaseOption.$states.HOVER) || "standard",
        textDecoration: this.getOptionValue(TextDecoration),
        textDecorationHover: this.getOptionValue(TextDecoration, BaseOption.$states.HOVER),
        textStyle: this.getOptionValue(FontStyle),
        textStyleHover: this.getOptionValue(FontStyle, BaseOption.$states.HOVER),
      };
    },
    spacingStyleOverrides() {
      return {
        paddingTop: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "top") || "none",
        paddingBottom: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "bottom") || "none",
        paddingLeft: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "left") || "none",
        paddingRight: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "right") || "none",
        marginTop: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "top") || "none",
        marginBottom: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "bottom") || "none",
        marginLeft: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "left") || "none",
        marginRight: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "right") || "none",
        paddingHoverTop: this.getOptionValue(Padding, BaseOption.$states.HOVER, "top") || "none",
        paddingHoverBottom: this.getOptionValue(Padding, BaseOption.$states.HOVER, "bottom") || "none",
        paddingHoverLeft: this.getOptionValue(Padding, BaseOption.$states.HOVER, "left") || "none",
        paddingHoverRight: this.getOptionValue(Padding, BaseOption.$states.HOVER, "right") || "none",
        marginHoverTop: this.getOptionValue(Margins, BaseOption.$states.HOVER, "top") || "none",
        marginHoverBottom: this.getOptionValue(Margins, BaseOption.$states.HOVER, "bottom") || "none",
        marginHoverLeft: this.getOptionValue(Margins, BaseOption.$states.HOVER, "left") || "none",
        marginHoverRight: this.getOptionValue(Margins, BaseOption.$states.HOVER, "right") || "none",
      };
    },
    textStyleOverrides() {
      return {
        standardFontFamily: this.getOptionValue(FontFamily) || "standard",
        standardFontSize: normalizeFontSizeStyleValue(this.getOptionValue(FontSize), { element: "TextElement", option: "font-size" }),
        standardFontColor: this.getOptionValue(FontColor) || "standard",
        standardFontWeight: this.getOptionValue(FontWeight) || "standard",
        standardHoverFontFamily: this.getOptionValue(FontFamily, BaseOption.$states.HOVER) || "standard",
        standardHoverFontSize: normalizeFontSizeStyleValue(this.getOptionValue(FontSize, BaseOption.$states.HOVER), { element: "TextElement", option: "hover.font-size" }),
        standardHoverFontColor: this.getOptionValue(FontColor, BaseOption.$states.HOVER) || "standard",
        standardHoverFontWeight: this.getOptionValue(FontWeight, BaseOption.$states.HOVER) || "standard",
        ...this.spacingStyleOverrides,
        backgroundColor: this.getOptionValue(BackgroundColor),
        textDecoration: this.getOptionValue(TextDecoration),
        textDecorationHover: this.getOptionValue(TextDecoration, BaseOption.$states.HOVER),
        textStyle: this.getOptionValue(FontStyle),
        textStyleHover: this.getOptionValue(FontStyle, BaseOption.$states.HOVER),
        hoverBackgroundColor: this.getOptionValue(BackgroundColor, BaseOption.$states.HOVER),
        boxShadow: this.getOptionValue(BoxShadow),
        boxShadowHover: this.getOptionValue(BoxShadow, BaseOption.$states.HOVER),
        textShadow: this.getOptionValue(TextShadow),
        textShadowHover: this.getOptionValue(TextShadow, BaseOption.$states.HOVER),
      };
    },
    textIsEmpty() {
      return this.initialValue.body_text === "\n" || !this.initialValue.body_text;
    },
    textOptionValue() {
      let textValue = this.getOptionValue(this.textValueOptionClass) || "";
      if (this.textValueKey && textValue) {
        // handle backwards compat
        if (this.textValueKey === "label") {
          // A present label wins even if hasUpdated was lost; fall back to the
          // legacy Label option only when the label is genuinely empty.
          const label = textValue[this.textValueKey];
          const hasLabel = typeof label === "string" && label.length > 0;
          textValue = textValue.hasUpdated || hasLabel
            ? label
            : this.getOptionValue(Label);
        } else {
          textValue = textValue[this.textValueKey];
        }
      }

      return textValue;
    },
    previewMode() {
      // O(1) per-key Set membership instead of an O(N) global array scan.
      return !this.editable || !useDynamicComponentEditorStore().selectionSet.has(String(this.identifier));
    },
    // Plain-text projection of the persisted text-option body for the
    // pre-edit iText render. While the element is empty and not actively
    // being edited the template renders this string (or the placeholder
    // fallback) instead of mounting iRichText, so the user sees the same
    // text they'd see in the production variant. Once they double-click,
    // `editable` flips and iRichText takes over with full rich-text
    // formatting; it stays mounted as long as content exists.
    initialValueText() {
      if (!this.initialValue) {
        return "";
      }
      if (typeof this.initialValue === "string") {
        return this.initialValue;
      }
      // iRichText emits `{body_text: "\n", ranges: []}` when the user
      // clears all text. Treat that as empty so the iText branch's
      // `initialValueText || placeholderText` fallback resolves to the
      // placeholder instead of rendering a stray newline.
      if (this.initialValue.body_text === "\n") {
        return "";
      }
      if (typeof this.initialValue.body_text === "string") {
        return this.initialValue.body_text;
      }
      return "";
    },
    hasTextContent() {
      if (typeof this.initialValue === "string") {
        return this.initialValue.length > 0;
      }
      return this.initialValue.body_text.length > 0 && this.initialValue.body_text !== "\n";
    },
    // Empty-state hint for the pre-edit iText branch. A freshly-dropped
    // text element has `TextOption.defaultValue === ""`, so without a
    // fallback string the iText slot renders nothing and the element
    // collapses to 0×0 on the canvas — un-clickable, only selectable
    // from the layers panel. Resolves to the panel alias when available
    // so Heading shows "Heading", Title shows "Title", etc.
    placeholderText() {
      return this.$options.elementPanelSettings?.alias
        || this.$options.elementPanelSettings?.displayName
        || "Add text";
    },
    // A rendered value is "present" when it is not null/undefined/"" — NOT
    // merely when it is truthy. The numeric elements (Price above all) legally
    // resolve to 0, and `value || placeholder` used to swallow that, so a free
    // plan rendered its currency symbol with no amount beside it.
    hasTextValue() {
      return this.value !== null && this.value !== undefined && this.value !== "";
    },
    displayText() {
      if (this.hasTextValue) {
        return this.value;
      }
      return this.editMode ? this.placeholderText : "";
    },
    textValueOptionClass() {
      return TextOption;
    },
    textValueKey() {
      return undefined;
    },
    myBorderSides() {
      // A missing BorderSides leaf must degrade to "no border" — the
      // template and iText's border-css helper deref each side, so undefined crashes.
      return this.getOptionValue(BorderSides) ?? NO_TEXT_BORDER_SIDES;
    },
    // Entity-page link seam: entity-bound subclasses (the DynamicApp*
    // elements) override this to the resolved display-page URL of their
    // bound entity (the `link-to-entity-page` option). When set it takes
    // precedence over an authored ClickAction in actionType / clickValue
    // below. Base text elements have no entity context — null.
    entityPageHref() {
      return null;
    },
    actionType() {
      if (this.entityPageHref) {
        return "goToUrl";
      }
      return this.getOptionValue(ClickAction)?.action;
    },
    // Inline rather than a scoped class: subclasses (Label, Heading, ...) inherit
    // this template through `extends` but render under their own scope id.
    clickableStyle() {
      return this.hasClickValue ? { cursor: "pointer" } : null;
    },
    hasClickValue() {
      if (this.editMode) {
        return false;
      }

      return this.clickValue !== null && this.clickValue?.length > 0;
    },
    clickValue() {
      if (this.entityPageHref) {
        return this.entityPageHref;
      }
      let urlValue = this.getOptionValue(ClickAction)?.value;

      if (!this.linkType) {
        return;
      }

      switch (this.linkType) {
        case LinkType.LINK_TYPE_HYPERLINK:
          return urlValue;
        case LinkType.LINK_TYPE_PAGE: {
          urlValue = pageLinkValueFor(this.getOptionValue(ClickAction), urlValue, this.scopedProvidedContexts, { quiet: isBuilderQuiet(this) });
          if (urlValue === null) {
            return;
          }
          const contextHref = this.contextResolvedHref(
            this.getOptionValue(ClickAction), urlValue, this.links.web,
          );
          if (contextHref) {
            return contextHref;
          }
          urlValue = resolveContextualPageValue(urlValue);
          if (urlValue === null || urlValue === undefined) {
            return;
          }
          if (urlValue.startsWith("/")) {
            urlValue = urlValue.substring(1);
          }
          const pageHref = `${this.links.web}/${urlValue}`;
          return hasUnfilledPlaceholder(pageHref) ? undefined : pageHref;
        }
        case LinkType.LINK_TYPE_STORY:
          return `${this.links.web}/story/${urlValue}`;
        case LinkType.LINK_TYPE_HASHTAG:
          return `${this.links.web}/stories/${urlValue}`;
        default:
          throw new DynamicComponentError("Url type not found", {
            linkType: this.linkType,
            urlValue,
          });
      }
    },
    linkType() {
      return this.getOptionValue(ClickAction)?.linkType;
    },
    openInNewTab() {
      return this.getOptionValue(ClickAction)?.openInNewTab;
    },
  },
  watch: {
    textOptionValue: {
      immediate: true,
      handler(newValue) {
        // While inline-editing, the editor owns its content; syncing the
        // round-tripped option value back would churn the contenteditable.
        if (this.editable) {
          return;
        }
        this.initialValue = newValue;
      },
    },
    previewMode(newValue) {
      if (newValue) {
        this.$refs.richTextElement.commands.closeBubbleMenu();
        useDynamicComponentEditorStore().removeElementFromEditingInline(this.identifier);
      }
    },
  },
  created() {
    // this.setDefaultBrandingValue();
  },
  methods: {
    ...mapActions(useDynamicComponentEditorStore, [
      "findElementInLayout",
      "handleOnEdit",
    ]),
    handleLinkModalOpen(event) {
      this.linkData = event;
      this.originalLinkData = event;
      this.showLinkModal = true;
    },
    handleLinkModalClose() {
      this.linkData = {};
      this.originalLinkData = {};
      this.showLinkModal = false;
    },
    setDefaultBrandingValue() {
      if (!this.configuration.options["default.text-branding-style-option"]) {
        this.configuration.options["default.text-branding-style-option"] = "paragraph";
      }
    },
    handleBlur() {
      this.editable = false;
      // Flush any pending debounced write so leaving the field persists at once.
      if (this.textWriteTimer) {
        clearTimeout(this.textWriteTimer);
        this.textWriteTimer = null;
        this.commitTextOption(this.initialValue);
      }
    },
    handleEnter(event) {
      if (event.shiftKey) {
        this.handleBlur();
      }
    },
    handleClick(event) {
      // On the builder canvas (including buttons / texts inside a
      // templated component embed), suppress the configured click action
      // so it doesn't fire mid-edit. Without `inBuilderEditScope`, an
      // embedded text in a templated component would mount with
      // editMode=false (DCPE forces it) and fall into `handleClickAction`
      // below.
      if (this.inBuilderEditScope && !this.editMode) {
        return;
      }
      if (this.editMode && this.canEditPublication) {
        this.setElementToInspect(event);
        if (Date.now() - this.lastClicked < CLICK_BUFFER && !this.editable) {
          this.editable = true;
          this.relatedElement = { ...this.dynamicComponentInstance.getElement(this.identifier) };
          useDynamicComponentEditorStore().setElementAsEditingInline(this.identifier);
          requestAnimationFrame(() => {
            const editableContent = this.$el.querySelector(".i-text.editor-content");
            if (editableContent) {
              editableContent.focus();

              const range = document.createRange();
              range.selectNodeContents(editableContent);

              const selection = window.getSelection();
              selection.removeAllRanges();
              selection.addRange(range);
            }
          });
        }
        this.lastClicked = Date.now();
      } else {
        this.handleClickAction();
      }
    },
    handleClickAction() {
      // A click that is authored as a gesture belongs to the gesture engine,
      // which runs the same action: doing it here as well navigates twice, or
      // opens two tabs.
      if (this.gestureOwnsTheClick) {
        return;
      }
      if (suppressesNavigation(this)) {
        return;
      }
      if (this.actionType === "goToUrl") {
        const a = document.createElement("a");
        if (this.openInNewTab) {
          a.target = "_blank";
        }

        if (!this.clickValue) {
          return;
        }

        a.href = this.clickValue;
        // A detached anchor ignores target="_blank" — it has to be in the
        // document for the browser to honour it.
        if (this.openInNewTab) {
          a.rel = "noopener";
          document.body.appendChild(a);
          a.click();
          a.remove();
          return;
        }
        a.click();
      } else {
        // ignore for now
      }
    },
    setElementToInspect(event) {
      event.stopPropagation();
      this.handleOnEdit(this.identifier, event);
      useDynamicComponentEditorStore().setActiveBreakpointByAlias(this.breakpoint);
    },
    handleTextOptionUpdate(value) {
      if (!this.editMode) {
        return;
      }
      // Keep the editor's local model in lockstep (instant, no re-sync churn)
      // and debounce the heavy option write so typing stays responsive.
      this.initialValue = value;
      if (this.textWriteTimer) {
        clearTimeout(this.textWriteTimer);
      }
      this.textWriteTimer = setTimeout(() => {
        this.textWriteTimer = null;
        this.commitTextOption(value);
      }, TEXT_WRITE_DEBOUNCE_MS);
    },
    commitTextOption(value) {
      let valueToUpdate = value;

      if (this.textValueKey) {
        valueToUpdate = JSON.parse(JSON.stringify(this.getOptionValue(this.textValueOptionClass)));
        valueToUpdate[this.textValueKey] = value;
        valueToUpdate["hasUpdated"] = true;
      }

      const [state, breakpoint] = useDynamicComponentEditorStore().activeState.split(":");
      // Write to whichever option key the renderer reads from. For
      // text elements (`textValueOptionClass = TextOption`) that's
      // "text-option"; for elements that piggyback on a wrapping
      // option (UrlElement uses `HrefAction.label`, so its
      // `textValueOptionClass` is HrefAction) the write must land on
      // that wrapping option, otherwise the rich-text edit silently
      // writes to a key the renderer doesn't read and the canvas
      // text never updates.
      const Klass = this.textValueOptionClass;
      const targetOptionKey = Klass?.optionKey
        ?? Klass?.name?.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase()
        ?? "text-option";
      DynamicElement.get(this.identifier)?.updateOption(
        targetOptionKey,
        breakpoint,
        state,
        valueToUpdate,
      );
    },
  },
};
